import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import SimpleBarReact from "simplebar-react";
import { useMutation, useQuery } from "@apollo/client";
import { styled } from "@mui/material/styles";

import AuthLayout from "../../services/auth/jwt/withAuth";
import { Message } from "../../types/models/Chat";
import {
  GET_MEMBER_CHAT_ROOM,
  GET_MESSAGES,
} from "../../graphql/queries/message";
import {
  CREATE_MESSAGE_NOTIFICATION,
  SEND_MESSAGE,
} from "../../graphql/mutations/message";
import SocketClient from "../../services/SocketClient";
import { SOCKET_CLIENT } from "../../constants/socket";
import { showErrorToast } from "../../components/apps/AppError";
import MessageList from "./components/MessageList";
import { components } from "../../components";
import MessageInput from "./components/MessageInput";
import { useQueryProfile } from "../../graphql/apollo/useQuery";
import { currentBranch } from "../../constants/constants";

const MessagesScreen = styled("div")(() => {
  return {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  };
});

const ScrollbarWrapper = styled(SimpleBarReact)(() => {
  return {
    display: "flex",
    flexDirection: "column",
    height: `100px`,
  };
});

export const ChatScreen: React.FC = () => {
  const { t } = useTranslation();
  let _scrollBarRef = useRef<any>(null);

  const renderHeader = (): JSX.Element => {
    return <components.Header title={t("customerService")} goBack={true} />;
  };

  const { data: { getMemberChatRoom } = {} } = useQuery(GET_MEMBER_CHAT_ROOM);
  const { data, refetch } = useQuery(GET_MESSAGES);

  const { myProfile } = useQueryProfile();

  const [createNotification] = useMutation(CREATE_MESSAGE_NOTIFICATION, {
    onCompleted: (data) => {
      if (data.createChatNotification) {
        SocketClient.emit(
          SOCKET_CLIENT.SEND_CHAT_NOTIFICATION,
          data.createChatNotification
        );
      }
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const [sendMessage] = useMutation(SEND_MESSAGE, {
    onCompleted: (data) => {
      if (data.sendMessage) {
        SocketClient.emit(SOCKET_CLIENT.SEND_MESSAGE, data.sendMessage);
        createNotification({
          variables: {
            input: {
              member: myProfile?.id,
              message: data.sendMessage.id,
              toAdmin: true,
              branch: currentBranch,
            },
          },
        });
        refetch();
      }
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  useEffect(() => {
    SocketClient.on(SOCKET_CLIENT.RECIEVE_MESSAGE, (data) => {
      if (data.chatRoom.id === getMemberChatRoom?.id) {
        refetch();
      }
    });
  }, [getMemberChatRoom, refetch]);

  const messages: Message[] = data?.getMessages;

  return (
    <AuthLayout>
      {renderHeader()}
      <MessagesScreen>
        <ScrollbarWrapper ref={_scrollBarRef}>
          <MessageList messages={messages} />
        </ScrollbarWrapper>
        <Box
          sx={{
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            position: "fixed",
            bottom: 0,
          }}
        >
          <MessageInput onSendMessage={sendMessage} />
        </Box>
      </MessagesScreen>
    </AuthLayout>
  );
};
